
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { UserWithRoles, AppRole } from '@/types/supabase';
import { fetchUsersWithRoles, addUserRole, removeUserRole } from '@/services/roleManagementService';
import { toast } from 'sonner';
import { Loader2, UserPlus, UserCheck, UserMinus, Shield, GraduationCap, User } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const UserManagement = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is admin, if not redirect
    if (!isAdmin()) {
      toast.error("You don't have permission to access this page");
      navigate('/dashboard');
      return;
    }

    const loadUsers = async () => {
      try {
        setIsLoading(true);
        const usersWithRoles = await fetchUsersWithRoles();
        setUsers(usersWithRoles);
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [isAdmin, navigate]);

  const handleAddRole = async (userId: string, newRole: AppRole) => {
    try {
      setIsUpdating(userId);
      await addUserRole(userId, newRole);
      
      // Update the local state after successful API call
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId && !user.roles.includes(newRole)) {
            return {
              ...user,
              roles: [...user.roles, newRole]
            };
          }
          return user;
        })
      );
    } catch (error) {
      console.error(`Error adding ${newRole} role:`, error);
    } finally {
      setIsUpdating(null);
    }
  };

  const handleRemoveRole = async (userId: string, roleToRemove: AppRole) => {
    try {
      setIsUpdating(userId);
      await removeUserRole(userId, roleToRemove);
      
      // Update the local state after successful API call
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              roles: user.roles.filter(role => role !== roleToRemove)
            };
          }
          return user;
        })
      );
    } catch (error) {
      console.error(`Error removing ${roleToRemove} role:`, error);
    } finally {
      setIsUpdating(null);
    }
  };

  const getRoleBadge = (role: AppRole) => {
    switch (role) {
      case 'admin':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
            <Shield className="mr-1 h-3 w-3" /> Admin
          </Badge>
        );
      case 'mentor':
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
            <GraduationCap className="mr-1 h-3 w-3" /> Mentor
          </Badge>
        );
      case 'student':
        return (
          <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
            <User className="mr-1 h-3 w-3" /> Student
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage user roles and permissions</p>
        </div>
        <Button onClick={() => navigate('/admin/users/create')}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            View and manage user roles across the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((userItem) => (
                <TableRow key={userItem.id}>
                  <TableCell className="font-medium">
                    {userItem.profile?.first_name && userItem.profile?.last_name
                      ? `${userItem.profile.first_name} ${userItem.profile.last_name}`
                      : 'Unnamed User'}
                  </TableCell>
                  <TableCell>{userItem.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {userItem.roles.map(role => (
                        <div key={`${userItem.id}-${role}`} className="flex items-center">
                          {getRoleBadge(role)}
                          {userItem.roles.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveRole(userItem.id, role)}
                              disabled={isUpdating === userItem.id}
                            >
                              <UserMinus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={isUpdating === userItem.id}
                        >
                          {isUpdating === userItem.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Add Role
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!userItem.roles.includes('admin') && (
                          <DropdownMenuItem onClick={() => handleAddRole(userItem.id, 'admin')}>
                            <Shield className="mr-2 h-4 w-4" />
                            <span>Make Admin</span>
                          </DropdownMenuItem>
                        )}
                        {!userItem.roles.includes('mentor') && (
                          <DropdownMenuItem onClick={() => handleAddRole(userItem.id, 'mentor')}>
                            <GraduationCap className="mr-2 h-4 w-4" />
                            <span>Make Mentor</span>
                          </DropdownMenuItem>
                        )}
                        {!userItem.roles.includes('student') && (
                          <DropdownMenuItem onClick={() => handleAddRole(userItem.id, 'student')}>
                            <User className="mr-2 h-4 w-4" />
                            <span>Make Student</span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
